/**
 * Deterministic playtest bot.
 *
 * ## Why
 *
 * `reachability.test.ts` proves a course *can* be finished by flying isolated
 * jump arcs through the collision grid. That is not a playthrough: it never
 * touches an enemy, never collects anything, and never has to recover from a
 * bad landing. This bot closes that gap by playing a course frame by frame
 * through the **real** simulation — the same `Player`, `Level` and
 * `resolveInteractions` the shell runs — with no teleports, `godMode`,
 * `noClip` or direct position edits. If the bot clears a course, the real
 * physics and rules let a player through.
 *
 * ## What it is not
 *
 * It is a coarse, greedy policy, not a speedrunner. It walks toward a target
 * tile and jumps at walls, pits and enemies. Courses that need a specific
 * route — riding a lift, doubling back up a chimney — need a plan written as
 * data (`runPlan`); the bot will otherwise spend its budget in front of them.
 *
 * ## Determinism
 *
 * Every decision is a function of the level and player state. There is no
 * randomness and no wall-clock use, so a report is exactly reproducible and a
 * failure is a regression test.
 */

import { TILE } from '../../src/engine/constants'
import { ENEMY_KINDS } from '../../src/game/entities'
import { resolveInteractions, type Contact } from '../../src/game/interactions'
import { Level } from '../../src/game/level'
import { Player } from '../../src/game/player'
import { cloneLevelData } from '../../src/levels/storage'
import type { LevelData } from '../../src/game/types'

// ── mirrored shell constants ────────────────────────────────────────────────

/**
 * Spawn protection, mirroring `SPAWN_GRACE_FRAMES` in `main.ts`.
 *
 * Kept as a local constant rather than imported: `main.ts` is the application
 * shell and pulls in the renderer, DOM and editor. If either the value or the
 * point it is applied changes, this harness disagrees with the game and the
 * playtest test is where that shows up.
 */
export const SPAWN_GRACE_FRAMES = 150

/** Default whole-run frame budget: 200 seconds of game time at 60Hz. */
export const DEFAULT_BUDGET = 12000

// ── policy tuning ───────────────────────────────────────────────────────────

/** How far ahead of the leading edge a solid tile counts as a wall. */
const WALL_LOOKAHEAD_PX = 1
/** How far ahead of the leading edge the floor is probed for a pit. */
const PIT_LOOKAHEAD_PX = 4
/** An enemy inside this many tiles ahead is jumped. */
const ENEMY_JUMP_TILES = 2.6
/**
 * While descending, an enemy inside this many tiles ahead makes the bot brake
 * by holding the opposite direction. Landing right in front of an enemy is
 * fatal — a jump cannot clear it in the one or two frames of rise left — and
 * the fix is to land short of it and jump from a safe distance instead.
 */
const ENEMY_BRAKE_TILES = 3
/** Only enemies roughly level with the player are jumped. */
const ENEMY_VERTICAL_BAND_PX = TILE * 1.5
/** Grounded frames with no progress before the stuck jump fires. */
const STUCK_FRAMES = 20
/** Frames before another jump may be triggered, so one step is one jump. */
const JUMP_COOLDOWN = 4

/** Distance from a target within which the bot stops steering. */
const ARRIVE_PX = 3

// ── public types ────────────────────────────────────────────────────────────

export interface PlaytestOptions {
  /** Whole-run frame budget. Defaults to `DEFAULT_BUDGET`. */
  budget?: number
  /** Frames of spawn protection per life. Defaults to the shell's value. */
  spawnGrace?: number
  /** Hold the run button while moving. Default false: walk-speed arcs. */
  run?: boolean
  /**
   * Place the player at the level's spawn point and grant spawn grace in the
   * constructor, mirroring `App.loadLevel`. Default true, because a playtest
   * is "play this course as shipped". Pass false to drive some other state.
   */
  placeAtStart?: boolean
}

export interface PlaytestReport {
  course: string
  won: boolean
  frames: number
  deaths: number
  /**
   * Furthest reach, in tiles. A fraction, because sub-tile progress is how you
   * tell "stuck at the wall" from "slowly grinding along it".
   */
  maxX: number
  shards: number
  score: number
}

/** One step of a course-specific plan, as data. */
export type PlaytestStep =
  | {
      kind: 'moveTo'
      tx: number
      /** Target row. Omit to mean "the target's tile column, any row". */
      ty?: number
      maxFrames?: number
      /** Hold run for this leg. Defaults to the bot's own setting. */
      run?: boolean
    }
  | { kind: 'stompNearest'; radius?: number; maxFrames?: number }
  | { kind: 'hop'; maxFrames?: number }
  | { kind: 'wait'; frames: number }

/** Options for one `moveTo` leg. */
export interface MoveToOptions {
  /**
   * Target row. With it, success means the body overlaps the whole target
   * tile, which is what a goal flag needs: flying over the flag's column is
   * not reaching the flag.
   */
  ty?: number
  /** Hold run for this leg, overriding the bot's own setting. */
  run?: boolean
}

interface Decision {
  left: boolean
  right: boolean
  jump: boolean
  run: boolean
}

interface Point {
  x: number
}

type Objective =
  | {
      kind: 'move'
      tx: number
      ty: number | undefined
    }
  | { kind: 'stomp'; radius: number }

// ── the bot ─────────────────────────────────────────────────────────────────

export class PlaytestBot {
  readonly level: Level
  readonly player: Player
  readonly budget: number
  readonly spawnGrace: number

  private readonly runHeld: boolean
  private objective: Objective | null = null
  /** Run override for the current leg, or null to use the bot's setting. */
  private runOverride: boolean | null = null
  private frames = 0
  private deathsCount = 0
  private maxXPx = 0
  private over = false
  private jumpPrev = false
  /** A jump has fired and has not yet reached its apex or landed. */
  private jumping = false
  /** Frames until a jump may be triggered again. */
  private jumpCooldown = 0
  /** Grounded frames with a direction held and no horizontal progress. */
  private stuck = 0
  /** Set by `hop` until the hop actually leaves the ground. */
  private forceJump = false
  private hopStarted = false
  /** Hostile contact seen on the most recent frame, for death reports. */
  private lastContact: Contact | null = null
  private readonly deathLog: string[] = []

  constructor(level: Level, player: Player, options: PlaytestOptions = {}) {
    this.level = level
    this.player = player
    this.budget = options.budget ?? DEFAULT_BUDGET
    this.spawnGrace = options.spawnGrace ?? SPAWN_GRACE_FRAMES
    this.runHeld = options.run ?? false

    player.bind(level)
    level.player = player
    if (options.placeAtStart !== false) {
      player.placeAt(level.spawnX, level.spawnY)
      player.invuln = this.spawnGrace
    }
    this.maxXPx = player.x
  }

  /** Frames simulated so far, across every life. */
  get frame(): number {
    return this.frames
  }

  get deaths(): number {
    return this.deathsCount
  }

  /** True once the course is won, lives are gone, or the budget ran out. */
  get finished(): boolean {
    return this.over
  }

  get won(): boolean {
    return this.level.status === 'won'
  }

  /** The interaction pass's contact on the most recent frame. */
  get contact(): Contact | null {
    return this.lastContact
  }

  /** One line per death, in order: what the harness saw when it happened. */
  get deathCauses(): readonly string[] {
    return this.deathLog
  }

  get report(): PlaytestReport {
    return {
      course: this.level.data.name,
      won: this.won,
      frames: this.frames,
      deaths: this.deathsCount,
      maxX: +(this.maxXPx / TILE).toFixed(1),
      shards: this.level.stats.coins,
      score: this.level.stats.score,
    }
  }

  // ── behaviours ────────────────────────────────────────────────────────────

  /** Set a movement objective without blocking; browser playback calls step at 60 Hz. */
  target(tx: number, opts: MoveToOptions = {}): void {
    this.objective = { kind: 'move', tx, ty: opts.ty }
    this.runOverride = opts.run ?? null
  }

  /**
   * Walk toward tile `tx` for at most `maxFrames`.
   *
   * Success is the player's body overlapping that tile. With `ty` given, the
   * whole tile must be overlapped — the flag on a goal needs that, because a
   * long jump can cross its column six tiles above it. The run keeps stepping
   * through deaths (respawn included), so a death costs frames rather than
   * ending the behaviour.
   */
  moveTo(
    tx: number,
    maxFrames = this.budget,
    opts: MoveToOptions = {},
  ): boolean {
    this.target(tx, opts)
    const deadline = this.frame + maxFrames
    while (this.frame < deadline && !this.over && !this.reached(tx, opts.ty)) {
      this.step()
    }
    this.objective = null
    this.runOverride = null
    return this.reached(tx, opts.ty)
  }

  /**
   * Stomp every enemy inside `radius` tiles of the player.
   *
   * Success is the radius being clear of live enemies. The bot steers at the
   * nearest one and jumps when it is close and level; an enemy that walks away
   * is chased until it turns. The frame budget bounds the attempt.
   */
  stompNearest(radius = 4, maxFrames = this.budget): boolean {
    this.objective = { kind: 'stomp', radius }
    const deadline = this.frame + maxFrames
    while (
      this.frame < deadline &&
      !this.over &&
      this.nearestEnemy(radius) !== null
    ) {
      this.step()
    }
    this.objective = null
    return this.nearestEnemy(radius) === null
  }

  /** Jump once and stay in the air until the landing (or `maxFrames`). */
  hop(maxFrames = 120): boolean {
    this.forceJump = true
    this.hopStarted = false
    const deadline = this.frame + maxFrames
    while (this.frame < deadline && !this.over) {
      this.step()
      if (this.hopStarted && this.player.onGround && this.player.vy >= 0) {
        this.forceJump = false
        return true
      }
    }
    this.forceJump = false
    return false
  }

  /** Stand still for `frames`. Returns false if the run ended first. */
  wait(frames: number): boolean {
    const deadline = this.frame + frames
    while (this.frame < deadline && !this.over) this.step()
    return !this.over
  }

  /**
   * Run a course-specific plan, as data.
   *
   * Stops at the first failing step and returns false. A plan is the honest
   * way to describe a route the coarse policy cannot find — a lift to ride, a
   * ledge to drop onto — without teaching the policy about one course's
   * geometry.
   */
  runPlan(steps: readonly PlaytestStep[]): boolean {
    for (const step of steps) {
      const ok =
        step.kind === 'moveTo'
          ? this.moveTo(step.tx, step.maxFrames, {
              ty: step.ty,
              run: step.run,
            })
          : step.kind === 'stompNearest'
            ? this.stompNearest(step.radius, step.maxFrames)
            : step.kind === 'hop'
              ? this.hop(step.maxFrames)
              : this.wait(step.frames)
      if (!ok || this.over) return false
    }
    return true
  }

  // ── the frame ─────────────────────────────────────────────────────────────

  /**
   * Advance exactly one simulation frame, in the same order as `App.updatePlay`
   * and with the same death bridges:
   *
   *   input -> player.update -> level.step -> resolveInteractions
   *         -> below-world kill -> dead-player kill -> respawn or game over
   *
   * Skipping the shell's 1.1-second death freeze is deliberate: the pause is
   * presentation, and a playtest wants frames, deaths and progress.
   */
  step(): void {
    if (this.over) return
    const { level, player } = this

    const decision = this.decide()
    player.input.left = decision.left
    player.input.right = decision.right
    player.input.up = false
    player.input.down = false
    player.input.run = decision.run
    player.input.jump = decision.jump
    player.input.jumpPressed =
      decision.jump && (!this.jumpPrev || this.forceJump)
    this.jumpPrev = decision.jump

    this.lastContact = null
    level.publishDynamicSolids()
    player.update(level, level)
    level.step()
    const contact = resolveInteractions(level, player, { god: false })
    if (contact !== null) this.lastContact = contact
    this.frames++

    if (player.vy < 0) {
      this.hopStarted = true
      this.forceJump = false
    }
    if (player.y > level.height * TILE + TILE * 2) level.kill()
    if (!player.alive) level.kill()

    // Record the last position before respawn moves the player back.
    if (player.x > this.maxXPx) this.maxXPx = player.x

    if (level.status === 'dead') {
      this.deathsCount++
      this.deathLog.push(this.describeDeath())
      // `Level.kill()` already decremented the life counter.
      if (level.stats.lives > 0) {
        level.respawn()
        player.invuln = this.spawnGrace
      }
      this.jumping = false
      this.jumpPrev = false
      this.jumpCooldown = 0
      this.stuck = 0
      if (level.stats.lives <= 0) this.over = true
    }

    if (level.status === 'won' || this.frames >= this.budget) this.over = true
  }

  /** Why the last death happened, from what the harness can see. */
  private describeDeath(): string {
    const p = this.player
    const below = p.y > this.level.height * TILE + TILE * 2
    const contact = this.lastContact
    const where = `${Math.floor(p.x / TILE)},${Math.floor(p.y / TILE)}`
    if (contact !== null) return `${contact.kind}:${contact.id} @${where}`
    if (below) return `pit @${where}`
    if (this.level.stats.time <= 0) return `timeout @${where}`
    return `unknown @${where}`
  }

  /** One frame's input, from the objective and the current geometry. */
  private decide(): Decision {
    const p = this.player
    let dir: -1 | 0 | 1 = 0

    if (this.objective !== null) {
      const target: Point | null =
        this.objective.kind === 'move'
          ? { x: this.objective.tx * TILE + TILE / 2 }
          : this.nearestEnemy(this.objective.radius)
      if (target !== null) {
        const dx = target.x - (p.x + p.w / 2)
        dir = dx > ARRIVE_PX ? 1 : dx < -ARRIVE_PX ? -1 : 0
      }
    }

    // Progress bookkeeping: grounded frames with a direction held but no
    // speed. Twenty of those is a step the wall probe cannot see (a lip, a
    // corner) and jumping is the only way out.
    if (p.onGround && dir !== 0 && Math.abs(p.vx) < 0.05) this.stuck++
    else this.stuck = 0

    // A lift is not floor. Walking off one mid-ride is a fall; a rider waits
    // until there is somewhere at their level to step onto. This is the whole
    // difference between riding the platform in Star Ride and diving into the
    // sky next to it, and it needs no sense of where the platform is going.
    if (dir !== 0 && this.ridingDynamic() && !this.groundBeside(dir)) dir = 0

    // Brake out of a fall that would land on top of an enemy. Holding the
    // opposite direction in the air uses the engine's normal air skid; there
    // is no drag in the air, so simply releasing the direction would change
    // nothing.
    if (
      !p.onGround &&
      p.vy > 0 &&
      dir !== 0 &&
      this.enemyWithin(dir, ENEMY_BRAKE_TILES)
    ) {
      dir = dir === 1 ? -1 : 1
    }

    let jump = false
    let triggered = false
    if (p.onGround) {
      if (this.jumpCooldown > 0) this.jumpCooldown--
      if (this.jumpCooldown === 0 || this.forceJump) {
        // A wall, a pit or an enemy is jumped as soon as it is seen. Kept as
        // three separate predicates because the answer is worth reporting
        // when a run gets stuck: only one of them should be true at a time.
        const blocked = dir !== 0 && this.wallAhead(dir)
        const pit = !blocked && dir !== 0 && this.pitAhead(dir)
        const enemy =
          !blocked &&
          !pit &&
          dir !== 0 &&
          this.enemyWithin(dir, ENEMY_JUMP_TILES)
        const stuck = this.stuck >= STUCK_FRAMES
        if (this.forceJump || blocked || pit || enemy || stuck) {
          jump = true
          triggered = true
          this.jumping = true
          this.jumpCooldown = JUMP_COOLDOWN
          this.stuck = 0
        }
      }
    }

    // A pending hop keeps pressing until it leaves the ground, so a landing
    // does not lose the request to the input buffer.
    if (!triggered && this.forceJump) jump = true

    // Hold the button through the rise for full jump height, releasing at the
    // apex or on landing so the variable jump does not hang.
    if (!triggered && this.jumping) {
      jump = !p.onGround && p.vy < 0
      if (p.onGround || p.vy >= 0) this.jumping = false
    }

    return {
      left: dir === -1,
      right: dir === 1,
      jump,
      run: (this.runOverride ?? this.runHeld) && dir !== 0,
    }
  }

  /** True when the player's body overlaps the target tile. */
  private reached(tx: number, ty?: number): boolean {
    const p = this.player
    if (p.x + p.w <= tx * TILE || p.x >= (tx + 1) * TILE) return false
    if (ty === undefined) return true
    return p.y + p.h > ty * TILE && p.y < (ty + 1) * TILE
  }

  /** A solid tile directly in front of the body at the player's rows. */
  private wallAhead(dir: -1 | 1): boolean {
    const p = this.player
    const probeX = dir > 0 ? p.x + p.w + WALL_LOOKAHEAD_PX : p.x - 1
    const tx = Math.floor(probeX / TILE)
    const ty0 = Math.floor(p.y / TILE)
    const ty1 = Math.floor((p.y + p.h - 1) / TILE)
    for (let ty = ty0; ty <= ty1; ty++) {
      if (this.level.isSolid(tx, ty)) return true
    }
    return false
  }

  /** True when the floor one step ahead of the feet has gone away. */
  private pitAhead(dir: -1 | 1): boolean {
    const p = this.player
    const probeX =
      dir > 0 ? p.x + p.w + PIT_LOOKAHEAD_PX : p.x - PIT_LOOKAHEAD_PX
    const tx = Math.floor(probeX / TILE)
    const ty = Math.floor((p.y + p.h + 1) / TILE)
    return !this.level.isSolid(tx, ty) && !this.level.isOneWay(tx, ty)
  }

  /** True when a live enemy is within `tiles` ahead and roughly level. */
  private enemyWithin(dir: -1 | 1, tiles: number): boolean {
    const p = this.player
    for (const e of this.level.entities) {
      if (!e.alive || !ENEMY_KINDS.has(e.kind)) continue
      const gap = dir > 0 ? e.x - (p.x + p.w) : p.x - (e.x + e.w)
      if (gap < -e.w || gap > tiles * TILE) continue
      if (
        e.y + e.h < p.y - ENEMY_VERTICAL_BAND_PX ||
        e.y > p.y + p.h + ENEMY_VERTICAL_BAND_PX
      ) {
        continue
      }
      return true
    }
    return false
  }

  /** True when the player is resting on a travelling solid. */
  private ridingDynamic(): boolean {
    const p = this.player
    if (!p.onGround) return false
    const feet = p.y + p.h
    for (const r of this.level.dynamicSolids()) {
      if (p.x + p.w <= r.x || p.x >= r.x + r.w) continue
      if (Math.abs(feet - r.y) <= 2) return true
    }
    return false
  }

  /** True when the tile beside the feet is a surface to step onto. */
  private groundBeside(dir: -1 | 1): boolean {
    const p = this.player
    const tx = Math.floor((dir > 0 ? p.x + p.w + 1 : p.x - 1) / TILE)
    const ty = Math.floor((p.y + p.h) / TILE)
    return this.level.isSolid(tx, ty) || this.level.isOneWay(tx, ty)
  }

  /** The closest live enemy within `radius` tiles, or null. */
  private nearestEnemy(radius: number): Point | null {
    const p = this.player
    let best: Point | null = null
    let bestGap = Infinity
    for (const e of this.level.entities) {
      if (!e.alive || !ENEMY_KINDS.has(e.kind)) continue
      const gap = Math.hypot(e.x - p.x, e.y - p.y)
      if (gap > radius * TILE) continue
      if (gap < bestGap) {
        bestGap = gap
        best = { x: e.x + e.w / 2 }
      }
    }
    return best
  }
}

/**
 * Build a bot for a course definition.
 *
 * The `LevelData` is cloned, so built-in courses are never mutated by a run (a
 * brick head-bump splices the object list) and every run starts from the same
 * state. The player is placed at the course's real start and given the same
 * spawn grace the shell grants.
 */
export function createPlaytestBot(
  data: LevelData,
  options: PlaytestOptions = {},
): PlaytestBot {
  const level = new Level(cloneLevelData(data))
  return new PlaytestBot(level, new Player(), options)
}
